/**
 * Cortex image-alt convention.
 *
 * The alt slot in `![alt](src)` carries optional layout hints prefixed with
 * colon-separated tokens. Supported today:
 *
 *   interactive:   render SVG as inline DOM (for CSS/hover interactivity)
 *   <number>:      render at N% width (relative to container)
 *
 * Hints may stack in any order:
 *   ![50:label](src)                → 50% width
 *   ![interactive:label](src)       → inline SVG, full width
 *   ![interactive:50:label](src)    → inline SVG, 50% width
 *   ![50:interactive:label](src)    → same (order-independent)
 *
 * Falling back gracefully matters: any markdown renderer that doesn't know
 * this convention still sees a valid `![alt](src)` — the hint is just
 * treated as part of the alt text. Cortex itself strips the hint from the
 * alt attribute on the rendered `<img>` so screen readers read the clean
 * label.
 */
export interface ImageAltParsed {
  /** Full width if undefined. When set, treated as percent (1–100). */
  widthPercent?: number;
  /** True when the `interactive:` prefix is present. */
  interactive: boolean;
  /** alt with all hints stripped — safe for aria-label / <img alt>. */
  cleanAlt: string;
}

export function parseImageAlt(alt: string | undefined | null): ImageAltParsed {
  const out: ImageAltParsed = { interactive: false, cleanAlt: "" };
  let s = (alt ?? "").trim();
  // Consume known prefixes in any order. Each prefix ends at the first ':'.
  // Stop as soon as a segment doesn't match a known hint — the rest is the
  // literal alt text (which may itself contain ':'). Trim after every strip
  // so "interactive: Foo" → "Foo" rather than " Foo", matching the prior
  // single-prefix behaviour.
  while (s.length > 0) {
    const colon = s.indexOf(":");
    if (colon < 0) break;
    const head = s.slice(0, colon);
    const rest = s.slice(colon + 1).trimStart();
    if (head === "interactive") {
      out.interactive = true;
      s = rest;
      continue;
    }
    if (/^\d{1,3}$/.test(head)) {
      const n = Number(head);
      if (n >= 1 && n <= 100) {
        out.widthPercent = n;
        s = rest;
        continue;
      }
    }
    break;
  }
  out.cleanAlt = s.trim();
  return out;
}

/** Build the CSS style chunk for a parsed width hint.
 *
 * Sized-down images (width < 100%) get centered on the line via
 * `display: block; margin: 0 auto` — matches the usual "shrunk
 * image floats to the middle" expectation. A 100% image is already
 * the full content width, so no margin handling is needed. */
export function imageWidthStyle(widthPercent?: number): React.CSSProperties | undefined {
  if (!widthPercent) return undefined;
  const base: React.CSSProperties = {
    maxWidth: `${widthPercent}%`,
    width: "100%",
    height: "auto",
  };
  if (widthPercent < 100) {
    base.display = "block";
    base.marginLeft = "auto";
    base.marginRight = "auto";
  }
  return base;
}

/** Replace any leading `N:` width hint in the alt with `widthPercent:`.
 * When widthPercent is undefined or 100, strips the hint entirely. */
export function applyWidthHint(alt: string | undefined | null, widthPercent: number | undefined): string {
  const { interactive, cleanAlt } = parseImageAlt(alt);
  const parts: string[] = [];
  if (interactive) parts.push("interactive");
  if (widthPercent && widthPercent < 100) parts.push(String(widthPercent));
  if (cleanAlt) parts.push(cleanAlt);
  // No hint AND no real alt — keep alt empty, not a stray colon.
  if (parts.length === 0) return "";
  // Single segment with no hint prefixes — just the alt text.
  if (!interactive && (!widthPercent || widthPercent >= 100)) return cleanAlt;
  return parts.join(":");
}
